import {
  type DoctrineContext,
  evaluateQualityDoctrine,
} from '@/lib/ad-copy/generate-grounded-ad-copy';
import {
  type AdReadinessCheckId,
  type AdReadinessFinding,
  checkAdReadiness,
} from '@/lib/content-doctrine/ad-readiness';
import { findUnevidencedFigures } from '@/lib/content-doctrine/unevidenced-figures';

/**
 * The Critique section's contract: what a person sees under "Critique this ad"
 * on the Concept design page, and the deterministic half that produces it.
 *
 * ── TWO HALVES, TWO DIFFERENT AUTHORITIES ───────────────────────────────────
 * `checks` are OBJECTIVE. They are the rubric and the claims gates Mora already
 * runs elsewhere (`checkAdReadiness`, `evaluateQualityDoctrine`,
 * `findUnevidencedFigures`), re-used here, never re-implemented. A check may be
 * `fail` only because the underlying rule is objective; everything taste-adjacent
 * stays `warn` (the severity discipline `ad-readiness.ts` states).
 *
 * `scores` are a MODEL'S JUDGMENT (`ad-critique-judge.ts`). They are never a
 * pass/fail and never block: a 3/10 is a note, not a gate. The type system
 * carries that: a score has no `status`, and `kind` is the literal 'judgment'.
 *
 * Nothing here predicts performance. The panel says what is on the screen and
 * what could be sharper, never what the ad will earn.
 */

export type CritiqueCheckStatus = 'pass' | 'fail' | 'warn';

/** An existing control on the design page that addresses a finding. */
export type CritiqueActionId = 'write_copy' | 'edit_image' | 'generate_size';

export interface CritiqueCheck {
  id: string;
  label: string;
  status: CritiqueCheckStatus;
  /** Why it passed, or what is wrong, in the operator's words. */
  detail: string;
  fix: string | null;
  action: CritiqueActionId | null;
}

export const CRITIQUE_DIMENSIONS = [
  {
    id: 'scroll_stop',
    label: 'Scroll-stop',
    question:
      'Would this image and its first line make someone in a feed pause, or does it read as every other ad?',
    action: 'edit_image',
  },
  {
    id: 'offer_clarity',
    label: 'Clarity of the offer',
    question: 'Can a stranger say what is being offered and what to do next after one read?',
    action: 'write_copy',
  },
  {
    id: 'proof_strength',
    label: 'Proof strength',
    question:
      'Does the ad show evidence for its promise (a figure, a named result, a quote) that the Concept and the evidence lines actually supply?',
    action: 'write_copy',
  },
  {
    id: 'audience_fit',
    label: 'Audience fit',
    question: 'Does the language and the situation depicted speak to the stated audience?',
    action: 'write_copy',
  },
  {
    id: 'legibility',
    label: 'Legibility at feed size',
    question:
      'At roughly phone-feed size, is there one clear focal point, and is any text on the image readable against its background?',
    action: 'edit_image',
  },
  {
    id: 'brand_fit',
    label: 'Brand fit',
    question: 'Does it look and sound like the brand described, with no stray marks or names?',
    action: 'edit_image',
  },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  question: string;
  action: CritiqueActionId;
}>;

export type CritiqueDimensionId = (typeof CRITIQUE_DIMENSIONS)[number]['id'];

/** One model-judged dimension. There is deliberately no pass/fail field. */
export interface CritiqueScore {
  kind: 'judgment';
  dimension: CritiqueDimensionId;
  label: string;
  /** 1 to 10, an integer. */
  score: number;
  /** What the model saw, one sentence. */
  observation: string;
  /** One concrete improvement, or null when it was withheld (see `withheldReason`). */
  improvement: string | null;
  withheldReason: string | null;
  /** What the improvement edits, as the judge stated it. Absent on results cached before it existed. */
  target?: CritiqueTarget | null;
  action: CritiqueActionId | null;
}

export type CritiqueTarget = 'image' | 'copy';

const IMAGE_WORDS =
  /\b(?:graphic|image|photo|picture|card|logo|colou?rs?|line art|overlay|scrim|background|subhead|subline|tile text)\b/i;
const COPY_WORDS =
  /\b(?:intro|caption|primary text|headline copy|headline text(?: field)?|text field|body copy|body text|opening line|first line|copy option|description|call to action wording)\b/i;

/**
 * Which part of the ad an improvement edits, read from its own words. Used only
 * when the judge did not say (results cached before `target` existed, or a
 * model answer that left it out). Both kinds of word, or neither, is unclear:
 * null, and the panel shows no button rather than the wrong one.
 */
export function classifyImprovementTarget(improvement: string): CritiqueTarget | null {
  const image = IMAGE_WORDS.test(improvement);
  const copy = COPY_WORDS.test(improvement);
  if (image === copy) return null;
  return image ? 'image' : 'copy';
}

/** The control for a judged improvement: the judge's target first, the classifier second, otherwise none. */
export function actionForScore(
  score: Pick<CritiqueScore, 'improvement' | 'target'>,
): CritiqueActionId | null {
  if (!score.improvement) return null;
  const target = score.target ?? classifyImprovementTarget(score.improvement);
  if (target === 'image') return 'edit_image';
  if (target === 'copy') return 'write_copy';
  return null;
}

export type CritiqueJudgeState = 'ok' | 'unavailable' | 'no_image';

export interface AdCritiqueResult {
  checks: CritiqueCheck[];
  scores: CritiqueScore[];
  judge: { state: CritiqueJudgeState; note: string | null };
  /** The copy that was critiqued, so the panel can show what the result describes. */
  copy: AdCritiqueCopy;
  createdAt: string;
}

export interface AdCritiqueCopy {
  intro: string;
  headline: string;
  description: string | null;
  cta: string | null;
}

export interface DeterministicCritiqueInput {
  copy: AdCritiqueCopy;
  adPlatform: 'meta' | 'linkedin';
  hasImage: boolean;
  brandName: string | null;
  /** Verbatim lines the copy may cite: the claims and customer quotes already authorised. */
  verifiableSources: readonly string[];
  avoidPhrases: readonly string[];
  doctrine: DoctrineContext;
  /** The Concept's own hook/summary, as the direction the copy was written to. */
  direction?: string;
}

interface Row {
  id: string;
  label: string;
  passDetail: string;
  readinessIds: readonly AdReadinessCheckId[];
  action: CritiqueActionId | null;
}

const READINESS_ROWS: readonly Row[] = [
  {
    id: 'image_attached',
    label: 'Image attached',
    passDetail: 'This size has an image.',
    readinessIds: ['creative_present'],
    action: 'generate_size',
  },
  {
    id: 'cta',
    label: 'Call to action',
    passDetail: 'A call to action is set.',
    readinessIds: ['cta_present'],
    action: 'write_copy',
  },
  {
    id: 'hook_fold',
    label: 'Hook lands before the fold',
    passDetail: 'The first sentence fits inside the length the platform shows before it cuts.',
    readinessIds: ['corny_hook_too_long'],
    action: 'write_copy',
  },
  {
    id: 'no_em_dash',
    label: 'No em dashes in the intro',
    passDetail: 'The intro has no em dashes.',
    readinessIds: ['corny_em_dash_present'],
    action: 'write_copy',
  },
  {
    id: 'no_invented_brand',
    label: 'No other brand named',
    passDetail: 'The copy names no brand other than this one.',
    readinessIds: ['no_invented_brand_mark'],
    action: 'write_copy',
  },
  {
    id: 'mechanism',
    label: 'Claim says how it works',
    passDetail: 'The intro gives a reason, not only an assertion.',
    readinessIds: ['corny_claim_missing_mechanism'],
    action: 'write_copy',
  },
  {
    id: 'narration',
    label: 'Names what the reader gets',
    passDetail: 'The copy is about the reader, not the product narrating its own process.',
    readinessIds: ['corny_self_referential_narration'],
    action: 'write_copy',
  },
  {
    id: 'description_length',
    label: 'Description length',
    passDetail: 'The description fits the length the platform shows.',
    readinessIds: ['corny_description_too_long'],
    action: 'write_copy',
  },
];

/** The checks the readiness rubric runs that this surface cannot evaluate (no destination, no audience mode). */
const READINESS_IDS_NOT_EVALUATED_HERE: readonly AdReadinessCheckId[] = [
  'destination_present',
  'destination_matches_brand',
  'promise_matches_destination',
  'audience_assumes_no_prior_contact',
  'outcome_is_measurable',
  'claims_are_evidenced',
  'corny_cta_mismatched_to_temperature',
  'creative_promise_echoed',
];

function toStatus(severity: AdReadinessFinding['severity']): CritiqueCheckStatus {
  return severity === 'fail' ? 'fail' : 'warn';
}

export function critiquePlatformLabel(platform: 'meta' | 'linkedin'): string {
  return platform === 'linkedin' ? 'LinkedIn' : 'Meta';
}

/**
 * The objective half. Pure and synchronous: the same copy and evidence always
 * give the same rows, which is what lets the route cache the result against a
 * hash of its inputs.
 */
export function runDeterministicCritique(input: DeterministicCritiqueInput): CritiqueCheck[] {
  const { copy, adPlatform } = input;
  const checks: CritiqueCheck[] = [];

  const readiness = checkAdReadiness(
    {
      primaryText: copy.intro,
      headline: copy.headline,
      description: copy.description ?? undefined,
      cta: copy.cta,
      creativeRef: input.hasImage ? 'present' : null,
      platform: adPlatform,
    },
    { brandName: input.brandName, evidenceOnFile: input.verifiableSources },
  );
  const findings = readiness.findings.filter(
    (finding) => !READINESS_IDS_NOT_EVALUATED_HERE.includes(finding.id),
  );

  for (const row of READINESS_ROWS) {
    // Not a copy field on this provider (LinkedIn renders its own button): the
    // rubric does not run `cta_present` there, so there is no row to pass.
    if (row.id === 'cta' && adPlatform === 'linkedin') continue;
    const hits = findings.filter((finding) => row.readinessIds.includes(finding.id));
    if (hits.length === 0) {
      checks.push({
        id: row.id,
        label: row.label,
        status: 'pass',
        detail: row.passDetail,
        fix: null,
        action: null,
      });
      continue;
    }
    for (const hit of hits) {
      checks.push({
        id: row.id,
        label: row.label,
        status: toStatus(hit.severity),
        detail: hit.detail,
        fix: hit.fix,
        action: row.action,
      });
    }
  }

  const fields =
    adPlatform === 'linkedin'
      ? [
          { field: 'commentary', text: copy.intro },
          { field: 'headline', text: copy.headline },
        ]
      : [
          { field: 'primary text 1', text: copy.intro },
          { field: 'headline 1', text: copy.headline },
        ];

  const doctrine = evaluateQualityDoctrine(
    fields,
    input.verifiableSources,
    input.direction,
    input.avoidPhrases,
    input.doctrine,
  );
  if (doctrine.length === 0) {
    checks.push({
      id: 'claims',
      label: 'Claims and specificity',
      status: 'pass',
      detail: `Nothing in the copy asserts more than the evidence on file, and the lead says something only this brand's evidence says.`,
      fix: null,
      action: null,
    });
  } else {
    for (const finding of doctrine) {
      checks.push({
        id: 'claims',
        label: 'Claims and specificity',
        status: finding.blocking ? 'fail' : 'warn',
        detail: finding.detail,
        fix: finding.fix,
        action: actionForFinding({ id: 'claims', fix: finding.fix, action: 'write_copy' }),
      });
    }
  }

  const figures = findUnevidencedFigures(
    fields,
    input.verifiableSources.join('\n'),
    'src/lib/content-doctrine/unevidenced-figures.ts',
  );
  if (figures.length === 0) {
    checks.push({
      id: 'figures',
      label: 'Figures are in the evidence',
      status: 'pass',
      detail: 'Every figure in the copy appears in the evidence on file.',
      fix: null,
      action: null,
    });
  } else {
    for (const figure of figures) {
      checks.push({
        id: 'figures',
        label: 'Figures are in the evidence',
        status: 'warn',
        detail: figure.detail,
        fix: 'Add the source to the Brief, or take the figure out.',
        action: null,
      });
    }
  }

  return checks;
}

const NEEDS_A_SOURCE = /\b(cite|add (?:the|a|its) source|source (?:it|the (?:figure|claim)))\b/i;

/**
 * The control that addresses a finding, or null when none of the page's controls
 * does. A figure the evidence does not carry is fixed by supplying evidence (the
 * Brief) or removing the figure; "Write more copy options" generates other
 * wording and does neither. Also applied when a cached result is rendered, so
 * results saved before this mapping existed do not keep the wrong link.
 */
export function actionForFinding(
  finding: Pick<CritiqueCheck, 'id' | 'fix' | 'action'>,
): CritiqueActionId | null {
  if (finding.id === 'figures') return null;
  if (finding.id === 'claims') {
    const instruction = (finding.fix ?? '').split(/The evidence says:/)[0] ?? '';
    if (NEEDS_A_SOURCE.test(instruction)) return null;
  }
  return finding.action;
}

export interface CritiqueActionGroup {
  action: CritiqueActionId;
  /** Labels of the findings this action addresses, in order, without repeats. */
  addresses: string[];
}

/** One entry per distinct action, listing each finding it addresses. */
export function groupCritiqueActions(
  items: ReadonlyArray<{ label: string; action: CritiqueActionId | null }>,
): CritiqueActionGroup[] {
  const groups = new Map<CritiqueActionId, string[]>();
  for (const item of items) {
    if (!item.action) continue;
    const labels = groups.get(item.action) ?? [];
    if (!labels.includes(item.label)) labels.push(item.label);
    groups.set(item.action, labels);
  }
  return [...groups].map(([action, addresses]) => ({ action, addresses }));
}

export function summariseChecks(checks: readonly CritiqueCheck[]): {
  failed: number;
  warned: number;
  passed: number;
} {
  return {
    failed: checks.filter((check) => check.status === 'fail').length,
    warned: checks.filter((check) => check.status === 'warn').length,
    passed: checks.filter((check) => check.status === 'pass').length,
  };
}
